import React, { useState, useContext, useEffect, useLayoutEffect } from 'react';
import {
  IonAccordion,
  IonAccordionGroup,
  IonAvatar,
  IonBadge,
  IonItem,
  IonLabel,
  IonList,
  IonSearchbar,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  IonText,
} from '@ionic/react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { ProfileContext } from '../../context/ProfileContext';
import { AuthContext } from '../../context/AuthContext';
import { LoadingScreen } from '../../components/LoadingScreen';
import { WebLayout } from '../../components/layouts/WebLayout';
import { JobSelectionModal } from '../../components/payments/JobSelectionModal';
import {
  getUnpaidWerkersForScheduler,
  getPaymentReceipts,
} from '../../functions/Fx_Payment';
import {
  UnpaidWerkerSummary,
  WerkerPaymentHistory,
  PaymentReceipt,
} from '../../types/payment';
import {
  formatCurrency,
  formatDate,
  getCurrentYearStart,
  getCurrentTimestamp,
} from '../../utils/paymentCalculations';

type PaymentSegment = 'unpaid' | 'history';

interface PaymentsLocationState {
  segment?: PaymentSegment;
  refresh?: boolean;
}

const PaymentsDesktop: React.FC = () => {
  const { currentUser, authReady } = useContext(AuthContext) as any;
  const { profile } = useContext(ProfileContext) as any;
  const history = useHistory();
  const location = useLocation<PaymentsLocationState>();

  const [activeSegment, setActiveSegment] = useState<PaymentSegment>(
    location.state?.segment || 'unpaid'
  );
  const [searchText, setSearchText] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [loadingStartTime, setLoadingStartTime] = useState<number>(Date.now());
  const [lastRefreshTime, setLastRefreshTime] = useState<number>(0); // Start at 0, will be set by pathname effect

  const [unpaidWerkers, setUnpaidWerkers] = useState<UnpaidWerkerSummary[]>([]);
  const [selectedWerker, setSelectedWerker] = useState<UnpaidWerkerSummary | null>(null);
  const [isJobSelectionModalOpen, setIsJobSelectionModalOpen] = useState(false);

  const [paymentHistory, setPaymentHistory] = useState<WerkerPaymentHistory[]>([]);
  const [dateRangeStart, setDateRangeStart] = useState<number>(getCurrentYearStart());
  const [dateRangeEnd, setDateRangeEnd] = useState<number>(getCurrentTimestamp());

  // Convert timestamp → YYYY-MM-DD for <input type="date">
  const toInputDate = (ts: number): string => {
    const d = new Date(ts);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  // Parse YYYY-MM-DD as local noon timestamp (avoids UTC off-by-one)
  const fromInputDate = (val: string): number => {
    const [y, m, d] = val.split('-').map(Number);
    return new Date(y, m - 1, d, 12, 0, 0).getTime();
  };

  // Helper to enforce minimum 1 second loading display time
  const finishLoading = () => {
    const elapsed = Date.now() - loadingStartTime;
    const remaining = Math.max(0, 1000 - elapsed);
    setTimeout(() => setIsLoading(false), remaining);
  };

  // Always refresh when navigating to Payments page (all navigation methods)
  // Use useLayoutEffect to run synchronously before paint, preventing flash of stale data
  useLayoutEffect(() => {
    if (location.pathname === '/payments') {
      setIsLoading(true);
      setLoadingStartTime(Date.now());
      setLastRefreshTime(Date.now());
      setDateRangeEnd(getCurrentTimestamp());
      
      // Handle segment from location state (e.g., Done button → History)
      if (location.state?.segment) {
        setActiveSegment(location.state.segment);
      }
    }
  }, [location.pathname, location.state?.segment]);

  // Load unpaid werkers
  useEffect(() => {
    // Don't run on initial mount (lastRefreshTime = 0), wait for pathname effect to set it
    if (!profile.UserId || activeSegment !== 'unpaid' || lastRefreshTime === 0) {
      return;
    }
    setIsLoading(true);
    setLoadingStartTime(Date.now());
    
    // Add delay if payment just completed to allow Firestore index propagation
    const queryDelay = sessionStorage.getItem('paymentJustCompleted') === 'true' ? 500 : 0;
    sessionStorage.removeItem('paymentJustCompleted');
    
    setTimeout(() => {
      getUnpaidWerkersForScheduler(profile.UserId)
        .then(werkers => {
          // Simple: always use what Firestore returns (no optimistic filtering)
          setUnpaidWerkers(werkers);
        })
        .catch((err) => console.error('Error loading unpaid werkers:', err))
        .finally(() => finishLoading());
    }, queryDelay);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.UserId, activeSegment, lastRefreshTime]);

  // Keep unpaid badge count fresh when on the history segment
  useEffect(() => {
    if (!profile.UserId || activeSegment === 'unpaid') return;
    getUnpaidWerkersForScheduler(profile.UserId)
      .then(werkers => {
        // Simple: always use what Firestore returns
        setUnpaidWerkers(werkers);
      })
      .catch(() => {});
  }, [profile.UserId, lastRefreshTime]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync selectedWerker with filtered unpaidWerkers after optimistic filtering
  // Ensures modal shows current data after partial payments
  useEffect(() => {
    if (selectedWerker && isJobSelectionModalOpen) {
      const updated = unpaidWerkers.find(w => w.werkerUid === selectedWerker.werkerUid);
      if (updated && updated !== selectedWerker) {
        // Werker still has unpaid jobs - update reference to filtered object
        setSelectedWerker(updated);
      } else if (!updated) {
        // All jobs were paid - close modal gracefully
        setSelectedWerker(null);
        setIsJobSelectionModalOpen(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unpaidWerkers, isJobSelectionModalOpen]);

  // Load payment history
  useEffect(() => {
    // Don't run on initial mount (lastRefreshTime = 0), wait for pathname effect to set it
    if (!profile.UserId || activeSegment !== 'history' || lastRefreshTime === 0) {
      return;
    }
    setIsLoading(true);
    setLoadingStartTime(Date.now());
    getPaymentReceipts(profile.UserId, { startDate: dateRangeStart, endDate: dateRangeEnd })
      .then(setPaymentHistory)
      .catch((err) => console.error('Error loading payment history:', err))
      .finally(() => finishLoading());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.UserId, activeSegment, dateRangeStart, dateRangeEnd, lastRefreshTime]);

  const filteredUnpaid = unpaidWerkers.filter((w) =>
    w.werkerName.toLowerCase().includes(searchText.toLowerCase())
  );
  const filteredHistory = paymentHistory.filter((w) =>
    w.werkerName.toLowerCase().includes(searchText.toLowerCase())
  );

  const handleWerkerClick = (werker: UnpaidWerkerSummary) => {
    setSelectedWerker(werker);
    setIsJobSelectionModalOpen(true);
  };

  const handleJobSelectionDismiss = (data?: any) => {
    setIsJobSelectionModalOpen(false);
    if (data?.selectedJobs) {
      history.push({
        pathname: '/payments/summary',
        state: { werkerData: selectedWerker, selectedJobs: data.selectedJobs },
      });
    }
    setSelectedWerker(null);
  };

  const handleReceiptClick = (receipt: PaymentReceipt) => {
    history.push(`/payments/receipt/${receipt.receiptId}`);
  };

  if (!authReady) return <LoadingScreen LoadingText="Loading" />;
  if (currentUser.uid === '') {
    return <Redirect to={{ pathname: '/login', search: '?returnUrl=/portal' }} />;
  }

  const inputDateStyle: React.CSSProperties = {
    padding: '6px 8px',
    borderRadius: '6px',
    border: '1px solid var(--ion-color-medium)',
    background: 'var(--ion-background-color)',
    color: 'var(--ion-text-color)',
    fontSize: '13px',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '13px',
    color: 'var(--ion-color-medium)',
    whiteSpace: 'nowrap',
  };

  return (
    <WebLayout title="Payments">
      <div style={{ height: '100%', overflowY: 'auto', padding: '16px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>

          {/* ── Controls row: segment + search + date range ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <IonSegment
              value={activeSegment}
              onIonChange={(e: any) => { setActiveSegment(e.detail.value); setSearchText(''); }}
              style={{ width: 'auto', flexShrink: 0 }}
            >
              <IonSegmentButton value="unpaid">
                <IonLabel>
                  Unpaid{' '}
                  {unpaidWerkers.length > 0 && (
                    <IonBadge color="danger" style={{ marginLeft: 4 }}>{unpaidWerkers.length}</IonBadge>
                  )}
                </IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="history">
                <IonLabel>History</IonLabel>
              </IonSegmentButton>
            </IonSegment>

            <IonSearchbar
              value={searchText}
              onIonChange={(e) => setSearchText(e.detail.value!)}
              placeholder={activeSegment === 'unpaid' ? 'Search werkers...' : 'Search history...'}
              style={{ flex: 1, minWidth: '180px', '--box-shadow': 'none' } as any}
              animated={false}
            />

            {activeSegment === 'history' && (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                <label style={labelStyle}>From</label>
                <input
                  type="date"
                  value={toInputDate(dateRangeStart)}
                  onChange={(e) => { if (e.target.value) setDateRangeStart(fromInputDate(e.target.value)); }}
                  style={inputDateStyle}
                />
                <label style={labelStyle}>To</label>
                <input
                  type="date"
                  value={toInputDate(dateRangeEnd)}
                  onChange={(e) => { if (e.target.value) setDateRangeEnd(fromInputDate(e.target.value)); }}
                  style={inputDateStyle}
                />
              </div>
            )}
          </div>

          {/* ── Summary totals ── */}
          {!isLoading && (
            <div style={{ marginBottom: '12px' }}>
              {activeSegment === 'unpaid' && unpaidWerkers.length > 0 && (
                <IonText color="medium">
                  <p style={{ margin: 0, fontSize: '13px' }}>
                    Total Unpaid: <strong>{formatCurrency(unpaidWerkers.reduce((s, w) => s + w.totalUnpaid, 0))}</strong>
                  </p>
                </IonText>
              )}
              {activeSegment === 'history' && paymentHistory.length > 0 && (
                <IonText color="medium">
                  <p style={{ margin: 0, fontSize: '13px' }}>
                    Total Paid: <strong>{formatCurrency(paymentHistory.reduce((s, w) => s + w.totalPaid, 0))}</strong>
                  </p>
                </IonText>
              )}
            </div>
          )}

          {/* ── Main content ── */}
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
              <IonSpinner name="crescent" />
            </div>

          ) : activeSegment === 'unpaid' ? (
            filteredUnpaid.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <IonText color="medium"><h3>All caught up!</h3><p>No unpaid jobs at this time.</p></IonText>
              </div>
            ) : (
              <IonList>
                {filteredUnpaid.map((werker) => (
                  <IonItem key={werker.werkerUid} button onClick={() => handleWerkerClick(werker)}>
                    <IonAvatar slot="start">
                      <img src={werker.werkerProfilePic || '/assets/profilePic.png'} alt={werker.werkerName} />
                    </IonAvatar>
                    <IonLabel>
                      <h2>{werker.werkerName}</h2>
                      <p>{werker.jobCount} job{werker.jobCount !== 1 ? 's' : ''}</p>
                    </IonLabel>
                    <IonText slot="end" color="primary">
                      <strong>{formatCurrency(werker.totalUnpaid)}</strong>
                    </IonText>
                  </IonItem>
                ))}
              </IonList>
            )

          ) : filteredHistory.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <IonText color="medium"><h3>No payment history</h3><p>No payments found in this date range.</p></IonText>
            </div>
          ) : (
            <IonAccordionGroup>
              {filteredHistory.map((wh) => (
                <IonAccordion key={wh.werkerUid} value={wh.werkerUid}>
                  <IonItem slot="header">
                    <IonAvatar slot="start">
                      <img src={wh.werkerProfilePic || '/assets/profilePic.png'} alt={wh.werkerName} />
                    </IonAvatar>
                    <IonLabel>
                      <h2>{wh.werkerName}</h2>
                      <p>{wh.receiptCount} payment{wh.receiptCount !== 1 ? 's' : ''}</p>
                    </IonLabel>
                    <IonText slot="end" color="success">
                      <strong>{formatCurrency(wh.totalPaid)}</strong>
                    </IonText>
                  </IonItem>
                  <IonList slot="content">
                    {wh.receipts.map((receipt) => (
                      <IonItem key={receipt.receiptId} button onClick={() => handleReceiptClick(receipt)}>
                        <IonLabel>
                          <h3>{formatDate(receipt.paymentDate)}</h3>
                          <p>{receipt.jobs.length} job{receipt.jobs.length !== 1 ? 's' : ''}</p>
                        </IonLabel>
                        <IonText slot="end" color="dark">
                          {formatCurrency(receipt.totalAmount)}
                        </IonText>
                      </IonItem>
                    ))}
                  </IonList>
                </IonAccordion>
              ))}
            </IonAccordionGroup>
          )}

        </div>
      </div>

      {selectedWerker && (
        <JobSelectionModal
          isOpen={isJobSelectionModalOpen}
          werkerData={selectedWerker}
          onDismiss={handleJobSelectionDismiss}
        />
      )}
    </WebLayout>
  );
};

export default PaymentsDesktop;
